import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { StorageService } from 'src/app/services/azure/storage/storage.service';
import { Router } from '@angular/router';
import { Subscription } from '../settings/components/monetization/components/membership/membership.model';
import { AppBLL } from 'src/app/app.bll';
@Injectable({
  providedIn: 'root',
})
export class EventStaffBLL {
  //#region Loaders Observables

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();

  private isAddContactModelOpen: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isAddContactModelOpen$: Observable<boolean> =
    this.isAddContactModelOpen.asObservable();

  private isUpdateContactModelOpen: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isUpdateContactModelOpen$: Observable<boolean> =
    this.isUpdateContactModelOpen.asObservable();


  private eventStaffs: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly eventStaffs$: Observable<any> = this.eventStaffs.asObservable();


  private eventStaff: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly eventStaff$: Observable<any> = this.eventStaff.asObservable();

  private creation_status: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);
  readonly creation_status$: Observable<string> =
    this.creation_status.asObservable();

  selectedContact$: Subject<any> = new Subject<any>();
  selectedAd$: Subject<any> = new Subject<any>();

  private prefillSchoolName: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly prefillSchoolName$: Observable<string> = this.prefillSchoolName.asObservable();

  public setPrefillSchoolName(name: string) {
    this.prefillSchoolName.next(name);
  }

  private prefillWithCurrentUser: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly prefillWithCurrentUser$: Observable<string> = this.prefillWithCurrentUser.asObservable();

  public setPrefillWithCurrentUser(contactInfo: any) {
    this.prefillWithCurrentUser.next(contactInfo);
  }

  //#endregion Data Observables

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private storage: StorageService,
    private appBll: AppBLL,
    private router: Router
  ) { }

  public getEventStaffs(params: { admin_id?: string, page?: number, limit?: number, search?: string, teaming_up_id?: string } = {}) {
    this.isLoading.next(true);

    let url = `field-events?type=get_all_event_staff`;

    if (params.admin_id) url += `&admin_id=${params.admin_id}`;
    if (params.page) url += `&page=${params.page}`;
    if (params.limit) url += `&limit=${params.limit}`;
    if (params.search) url += `&search=${params.search}`;
    if (params.teaming_up_id) url += `&teaming_up_id=${params.teaming_up_id}`;

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.eventStaffs.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public getEventStaff(id: string) {
    this.isLoading.next(true);

    let url = `field-events?type=get_event_staff&id=${id}`;

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.eventStaff.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public async createEventStaff(staff: any, created_by: string = null) {
    this.isLoading.next(true);

    let url = `field-events?type=post_event_staff`;

    const payload = {
      ...staff,
      created_by: created_by,
      field_event_id: staff.field_event_id || null
    };

    this.api
      .post(url, payload)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.isLoading.next(false);
          this.creation_status.next('success');
          this.toast.showToast('Success!', 'Event staff has been created.');
          this.toggleAddContactModel(false);
          this.selectedAd$.next(response?.contacts?.id || response?.event_staff?.id);
          this.selectedContact$.next(response?.data?.id);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public async deleteEventStaff(id: string, admin_id: string = null) {
    let url = `field-events?type=delete_event_staff&id=${id}`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api.delete(url).subscribe(
      async () => {
        this.getEventStaffs({ admin_id });
        this.toast.showToast('Success!', 'Event staff has been deleted.');
      },
      async (err) => {
        console.log('HTTP Error', err);
      }
    );
  }

  public async updateEventStaff(id: string, staff: any, admin_id: string = null) {
    let url = `field-events?type=update_event_staff`;

    this.isLoading.next(true);

    let update_payload = { ...staff, id: id };

    this.api
      .patch(url, update_payload)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.isLoading.next(false);
          this.toast.showToast('Success!', 'Event staff has been updated.');
          this.creation_status.next('success');
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public async bulkCreateEventStaffs(contacts: any[], admin_id: string = null, created_by: string = null) {
    this.isLoading.next(true);

    // Note: The new API doesn't explicitly mention bulk create, 
    // but I'll keep it updated to match the new field 'role' instead of 'event_job'
    // and use the new base route 'field-events'.
    let url = `field-events?type=post_bulk_event_staff_contacts`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    const payload = contacts.map(c => ({
      ...c,
      staff_role: c.staff_role || c.role || c.event_job, // fallback for safety
      created_by: created_by
    }));

    this.api
      .post(url, { contacts: payload })
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.isLoading.next(false);
          this.toast.showToast('Success!', `${contacts.length} contacts have been uploaded.`);
          this.getEventStaffs({ admin_id });
        },
        (err) => {
          this.isLoading.next(false);
          this.toast.showToast('Error', 'Failed to upload contacts. Please check the file and try again.');
        }
      );
  }

  toggleAddContactModel(isOpen: boolean) {
    this.isAddContactModelOpen.next(isOpen);
  }

  changeUpdateContactModelOpen(isOpen: boolean) {
    this.isUpdateContactModelOpen.next(isOpen);
  }

}

